/**
 * User Preferences - Strongly typed utility for managing user preferences in Stytch untrusted metadata
 *
 * This utility provides type-safe access to user preferences stored in Stytch's member.untrusted_metadata.
 * Preferences are persisted per-user and sync across devices.
 */

import type { Member } from '@stytch/vanilla-js'

// Define all application preferences with their types
export interface UserPreferences {
	theme: 'light' | 'dark'
	viewPreferences: {
		connections: 'table' | 'cards'
		locations: 'table' | 'cards'
		users: 'table' | 'cards'
		domains: 'table' | 'cards'
		phoneNumbers: 'table' | 'cards'
		dataSources: 'table' | 'cards'
		billingProfiles: 'table' | 'cards'
	}
}

// Default preferences
export const DEFAULT_PREFERENCES: UserPreferences = {
	theme: 'light',
	viewPreferences: {
		connections: 'table',
		locations: 'table',
		users: 'table',
		domains: 'table',
		phoneNumbers: 'table',
		dataSources: 'table',
		billingProfiles: 'table',
	},
}

/**
 * Get user preferences from Stytch member untrusted metadata
 */
export function getUserPreferences(member: Member | null | undefined): UserPreferences {
	if (!member?.untrusted_metadata) {
		return DEFAULT_PREFERENCES
	}

	const metadata = member.untrusted_metadata as Partial<UserPreferences>

	return {
		theme: metadata.theme || DEFAULT_PREFERENCES.theme,
		viewPreferences: {
			...DEFAULT_PREFERENCES.viewPreferences,
			...metadata.viewPreferences,
		},
	}
}

/**
 * Get a specific preference value with type safety
 */
export function getPreference<K extends keyof UserPreferences>(
	member: Member | null | undefined,
	key: K
): UserPreferences[K] {
	const preferences = getUserPreferences(member)
	return preferences[key]
}

/**
 * Get a specific view preference
 */
export function getViewPreference(
	member: Member | null | undefined,
	entity: keyof UserPreferences['viewPreferences']
): 'table' | 'cards' {
	const preferences = getUserPreferences(member)
	return preferences.viewPreferences[entity]
}

/**
 * Create an update payload for Stytch untrusted metadata
 * Merges with existing metadata to avoid overwriting other fields
 */
export function createPreferenceUpdate<K extends keyof UserPreferences>(
	member: Member | null | undefined,
	key: K,
	value: UserPreferences[K]
): Record<string, unknown> {
	const currentPreferences = getUserPreferences(member)
	const updatedPreferences: UserPreferences = {
		...currentPreferences,
		[key]: value,
	}

	return {
		untrusted_metadata: updatedPreferences,
	}
}

/**
 * Create an update payload for a specific view preference
 */
export function createViewPreferenceUpdate(
	member: Member | null | undefined,
	entity: keyof UserPreferences['viewPreferences'],
	view: 'table' | 'cards'
): Record<string, unknown> {
	const currentPreferences = getUserPreferences(member)
	const updatedViewPreferences = {
		...currentPreferences.viewPreferences,
		[entity]: view,
	}

	return createPreferenceUpdate(member, 'viewPreferences', updatedViewPreferences)
}
